import {
  capitalizeFirst,
  startsUpper,
} from "@/lib/tool-kit/style-checkers/a-words";
import {
  formatNumeral,
  MAX_WORD_NUMBER,
  type NumberWordRun,
  numberToWords,
  numberWordRuns,
  ordinalNumeral,
  ordinalToWords,
} from "@/lib/tool-kit/style-checkers/number-words";
import {
  countWords,
  headingFlags,
  insideAny,
  quotedSpans,
  type Sentence,
  type Span,
  splitSentences,
} from "@/lib/tool-kit/writing-analysis/text";

/**
 * Numerals or words? A checker for the general rules of two manuals.
 *
 * APA 7 (Publication Manual §§6.32–6.39): words for zero through nine, numerals for 10
 * and above, with numerals kept for measurements and units, statistics, times, dates,
 * ages, scores, exact sums of money and numbers after a noun ("Table 3"); words for a
 * number that starts a sentence and for common fractions.
 *
 * Chicago 18 (chapter 9, non-scientific text): spell out whole numbers from zero through
 * one hundred and round multiples of them (two hundred, fifty thousand, 3 million); use
 * numerals for other numbers; numerals with abbreviated units; "percent" spelled out after
 * a numeral. AP style differs (it spells out only one through nine) and is not checked.
 *
 * What is skipped, so the output stays quiet where the rules do not apply: decimals,
 * fractions and ratios, times and dates, years, page and table references, numbers after a
 * label noun ("Table 3", "Step 1"), statistics ("n = 5"), ranges, money with a symbol,
 * numbers attached to an abbreviated unit, list markers, citations, URLs, and quotations.
 * What it cannot see is meaning: "1 in 5" and "a score of 3" look the same to it as "3 apples".
 */

export type NumberStyle = "apa" | "chicago";

export type NumberIssueKind =
  | "spell-out"
  | "use-numeral"
  | "sentence-start"
  | "percent"
  | "unit"
  | "ordinal";

export interface NumberIssue extends Span {
  /** The number as written, with a percent sign or unit when the rule covers it. */
  text: string;
  kind: NumberIssueKind;
  /** What to write instead, or null when the sentence should be reworded. */
  suggestion: string | null;
  sentenceStart: number;
  sentence: string;
}

export interface NumberReport {
  words: number;
  sentences: number;
  /** Numbers found, whether or not they follow the rules. */
  numerals: number;
  inWords: number;
  issues: NumberIssue[];
  byKind: Record<NumberIssueKind, number>;
}

/* ------------------------------------------------------------------ word lists */

const LABELS: ReadonlySet<string> = new Set([
  "table",
  "tables",
  "figure",
  "figures",
  "fig",
  "step",
  "steps",
  "chapter",
  "chapters",
  "ch",
  "section",
  "sections",
  "sec",
  "appendix",
  "grade",
  "experiment",
  "study",
  "group",
  "phase",
  "trial",
  "item",
  "items",
  "question",
  "hypothesis",
  "model",
  "equation",
  "eq",
  "condition",
  "wave",
  "cohort",
  "year",
  "day",
  "week",
  "session",
  "part",
  "page",
  "pages",
  "p",
  "pp",
  "no",
  "nos",
  "vol",
  "volume",
  "issue",
  "level",
  "type",
  "stage",
  "version",
  "form",
  "scale",
  "line",
  "lines",
  "row",
  "column",
  "slide",
  "exhibit",
  "example",
  "rule",
  "article",
  "paragraph",
  "para",
  "track",
  "channel",
  "lecture",
  "unit",
  "module",
  "class",
  "season",
  "episode",
  "chart",
  "plate",
  "panel",
  "note",
  "footnote",
  "act",
  "scene",
  "book",
  "canto",
  "verse",
  "psalm",
  "route",
  "highway",
  "interstate",
  "room",
  "floor",
  "pg",
  "stat",
  "option",
  "choice",
  "plan",
  "scenario",
  "case",
  "task",
  "aim",
  "goal",
  "tier",
  "arm",
  "hr",
  "min",
  "scored",
  "score",
  "rated",
  "rating",
  "ranked",
  "rank",
]);

const AGE_LABELS: ReadonlySet<string> = new Set(["age", "aged", "ages"]);

const MONTHS: ReadonlySet<string> = new Set([
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
  "jan",
  "feb",
  "mar",
  "apr",
  "jun",
  "jul",
  "aug",
  "sep",
  "sept",
  "oct",
  "nov",
  "dec",
]);

const ABBREVIATED_UNITS =
  "mg|g|kg|lb|lbs|oz|mm|cm|m|km|ft|yd|mi|ml|mL|l|L|dl|dL|hr|hrs|h|min|s|sec|ms|Hz|kHz|MHz|GHz|kB|MB|GB|TB|mol|mmol|M|K|V|A|W|kW|kWh|J|kJ|cal|kcal|Pa|kPa|bar|mph|rpm|dB|ppm|bpm|mm²|cm²|m²|km²|m³|cm³|µm|μm|nm";

const FULL_UNITS: ReadonlySet<string> = new Set([
  "second",
  "seconds",
  "minute",
  "minutes",
  "hour",
  "hours",
  "day",
  "days",
  "week",
  "weeks",
  "month",
  "months",
  "year",
  "years",
  "decade",
  "decades",
  "milligram",
  "milligrams",
  "gram",
  "grams",
  "kilogram",
  "kilograms",
  "pound",
  "pounds",
  "ounce",
  "ounces",
  "millimeter",
  "millimeters",
  "centimeter",
  "centimeters",
  "meter",
  "meters",
  "kilometer",
  "kilometers",
  "millimetre",
  "millimetres",
  "centimetre",
  "centimetres",
  "metre",
  "metres",
  "kilometre",
  "kilometres",
  "mile",
  "miles",
  "inch",
  "inches",
  "foot",
  "feet",
  "yard",
  "yards",
  "liter",
  "liters",
  "litre",
  "litres",
  "milliliter",
  "milliliters",
  "gallon",
  "gallons",
  "degree",
  "degrees",
  "dollar",
  "dollars",
  "euro",
  "euros",
  "cent",
  "cents",
  "point",
  "points",
]);

const MONEY_WORDS: ReadonlySet<string> = new Set([
  "dollar",
  "dollars",
  "euro",
  "euros",
  "cent",
  "cents",
]);

/** "3 million": a numeral with a scale word is the accepted form in both manuals. */
const SCALE_FOLLOWERS: ReadonlySet<string> = new Set([
  "million",
  "billion",
  "trillion",
]);

const FRACTION_WORDS: ReadonlySet<string> = new Set([
  "half",
  "halves",
  "third",
  "thirds",
  "quarter",
  "quarters",
  "fourth",
  "fourths",
  "fifth",
  "fifths",
  "sixth",
  "sixths",
  "seventh",
  "sevenths",
  "eighth",
  "eighths",
  "ninth",
  "ninths",
  "tenth",
  "tenths",
]);

/* ------------------------------------------------------------------ patterns */

const RE_NUMERAL =
  /(?<![\p{L}\p{N}.,/:$€£¥-])(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?(?:st|nd|rd|th)?(?![\p{L}\p{N}])/gu;
const RE_IGNORED = new RegExp(
  [
    "https?:\\/\\/\\S+",
    "www\\.\\S+",
    "\\b10\\.\\d{4,}\\/\\S+",
    "\\[[\\d,\\s–-]+\\]",
    "\\([^()]*\\p{Lu}\\p{L}+[^()]*\\b(?:1[5-9]|20)\\d{2}[a-z]?\\b[^()]*\\)",
    "\\([^()]*\\bpp?\\.\\s?\\d[^()]*\\)",
  ].join("|"),
  "gu"
);
const RE_LEADING_OPENERS = /^["“‘'([\s]*/;
const RE_PERCENT = /^\s*(?:%|percent\b|per\s?cent\b)/i;
const RE_ABBREVIATED_UNIT = new RegExp(
  `^\\s?(?:${ABBREVIATED_UNITS}|°[CF]?)(?![\\p{L}\\p{N}])`,
  "u"
);
const RE_TIME_OF_DAY = /^(?::\d|\s?(?:[ap]\.?m\.?\b|o['’]clock))/i;
const RE_NEXT_WORD = /^[\s-]+(\p{L}+)/u;
const RE_PREVIOUS_WORD = /(\p{L}+)\.?\s*$/u;
const RE_STATISTIC_BEFORE = /[=<>≤≥±×+−−/]\s*$/;
const RE_STATISTIC_AFTER = /^\s*[×/]|^\s*(?:in|out of)\s+\d/;
const RE_RANGE_BEFORE = /\d\s*[-–—]\s*$/;
const RE_RANGE_AFTER = /^\s*[-–—]\s*\d/;
const RE_CURRENCY_BEFORE = /[$€£¥]\s*$/;
const RE_LIST_MARKER = /^[.)](?:\s|$)/;
const RE_NUMERAL_PARTS = /^([\d,]+)(?:\.(\d+))?(st|nd|rd|th)?$/;
const RE_COMMAS = /,/g;
const RE_ACRONYM = /^\p{Lu}{2,}$/u;
const RE_NUMBER_LIST_BEFORE = /(?:^|[(,]\s*)\d{1,3},\s*$/;
const RE_NUMBER_LIST_AFTER = /^\s*,\s*\d/;
const RE_PAREN_NUMBER_BEFORE = /\(\s*$/;
const RE_PAREN_NUMBER_AFTER = /^\s*\)/;
const RE_DAY_OF_MONTH_AFTER = /^\s+(?:of\s+)?(\p{L}+)/u;

/* ------------------------------------------------------------------ items */

interface Item extends Span {
  text: string;
  value: number;
  isWords: boolean;
  decimal: boolean;
  ordinal: boolean;
  scaleWord: string | null;
  upperInitial: boolean;
}

function numeralItems(text: string): Item[] {
  const items: Item[] = [];
  for (const match of text.matchAll(RE_NUMERAL)) {
    const parts = RE_NUMERAL_PARTS.exec(match[0]);
    if (!parts) {
      continue;
    }
    const whole = Number(parts[1].replace(RE_COMMAS, ""));
    items.push({
      start: match.index,
      end: match.index + match[0].length,
      text: match[0],
      value: parts[2] ? Number(`${whole}.${parts[2]}`) : whole,
      isWords: false,
      decimal: Boolean(parts[2]),
      ordinal: Boolean(parts[3]),
      scaleWord: null,
      upperInitial: false,
    });
  }
  return items;
}

function wordItems(text: string): Item[] {
  return numberWordRuns(text).map((run: NumberWordRun) => ({
    start: run.start,
    end: run.end,
    text: run.text,
    value: run.value,
    isWords: true,
    decimal: false,
    ordinal: run.ordinal,
    scaleWord: run.scaleWord,
    upperInitial: startsUpper(run.text),
  }));
}

/* ------------------------------------------------------------------ context */

interface Context {
  text: string;
  item: Item;
  /** True when the number is the first thing in its sentence. */
  sentenceStart: boolean;
  before: string;
  after: string;
  previousWord: string;
  /** The previous word as written, for telling a name from a common word. */
  previousRaw: string;
  /** The previous word is a name, an acronym or a capitalised label ("APA 7", "Apollo 11"). */
  previousIsName: boolean;
  nextWord: string;
}

function contextOf(text: string, item: Item, sentence: Sentence): Context {
  const beforeStart = Math.max(sentence.start, item.start - 40);
  const before = text.slice(beforeStart, item.start);
  const after = text.slice(item.end, Math.min(sentence.end, item.end + 40));
  const opener = RE_LEADING_OPENERS.exec(sentence.text)?.[0].length ?? 0;
  const previous = RE_PREVIOUS_WORD.exec(before);
  const previousRaw = previous?.[1] ?? "";
  const previousAtStart =
    previous !== null &&
    beforeStart + previous.index === sentence.start + opener;
  return {
    text,
    item,
    sentenceStart: item.start === sentence.start + opener,
    before,
    after,
    previousWord: previousRaw.toLowerCase(),
    previousRaw,
    previousIsName:
      startsUpper(previousRaw) &&
      (!previousAtStart || RE_ACRONYM.test(previousRaw)),
    nextWord: RE_NEXT_WORD.exec(after)?.[1]?.toLowerCase() ?? "",
  };
}

function isExemptContext(context: Context): boolean {
  const { before, after, previousWord, item } = context;
  return (
    RE_STATISTIC_BEFORE.test(before) ||
    RE_STATISTIC_AFTER.test(after) ||
    RE_RANGE_BEFORE.test(before) ||
    RE_RANGE_AFTER.test(after) ||
    RE_CURRENCY_BEFORE.test(before) ||
    RE_TIME_OF_DAY.test(after) ||
    RE_ABBREVIATED_UNIT.test(after) ||
    LABELS.has(previousWord) ||
    (!item.isWords && isNameOrList(context)) ||
    (!item.isWords && SCALE_FOLLOWERS.has(context.nextWord)) ||
    isDate(context) ||
    (item.isWords && isFraction(context))
  );
}

/** "APA 7", "Apollo 11", "(1, 3, 5)", "91(1)": a name with a number, a list of numbers, or a bracketed index. */
function isNameOrList(context: Context): boolean {
  const { before, after, previousIsName } = context;
  return (
    previousIsName ||
    RE_NUMBER_LIST_BEFORE.test(before) ||
    RE_NUMBER_LIST_AFTER.test(after) ||
    (RE_PAREN_NUMBER_BEFORE.test(before) && RE_PAREN_NUMBER_AFTER.test(after))
  );
}

function isDate(context: Context): boolean {
  const { previousWord, after, item } = context;
  if (MONTHS.has(previousWord)) {
    return true;
  }
  const next = RE_DAY_OF_MONTH_AFTER.exec(after)?.[1]?.toLowerCase() ?? "";
  return !item.isWords && MONTHS.has(next);
}

function isFraction(context: Context): boolean {
  return FRACTION_WORDS.has(context.nextWord);
}

/* ------------------------------------------------------------------ rules */

interface Verdict {
  kind: NumberIssueKind;
  suggestion: string | null;
  /** Extends the highlight to cover a percent sign or unit after the number. */
  coverAfter: number;
}

function percentLength(after: string): number {
  return RE_PERCENT.exec(after)?.[0].length ?? 0;
}

function numeralText(item: Item): string {
  return item.isWords ? formatNumeral(item.value, item.scaleWord) : item.text;
}

function percentVerdict(context: Context, style: NumberStyle): Verdict | null {
  const length = percentLength(context.after);
  if (length === 0) {
    return null;
  }
  const marker = context.after.slice(0, length).trim().toLowerCase();
  const symbol = marker === "%";
  const number = numeralText(context.item);
  if (style === "apa" && (symbol || !context.item.isWords)) {
    return symbol
      ? null
      : {
          kind: "percent",
          suggestion: `${number}%`,
          coverAfter: length,
        };
  }
  if (style === "chicago" && !symbol && !context.item.isWords) {
    return null;
  }
  const suggestion = style === "apa" ? `${number}%` : `${number} percent`;
  return {
    kind: "percent",
    suggestion,
    coverAfter: length,
  };
}

function startVerdict(context: Context): Verdict {
  const { item } = context;
  const length = percentLength(context.after);
  const yearLike =
    !item.decimal &&
    item.value >= 1000 &&
    item.value <= 2999 &&
    !item.text.includes(",");
  const spellable = !item.decimal && item.value <= MAX_WORD_NUMBER && !yearLike;
  const words = spellable
    ? capitalizeFirst(
        item.ordinal ? ordinalToWords(item.value) : numberToWords(item.value)
      )
    : "";
  let suggestion: string | null = words || null;
  if (words && length > 0) {
    suggestion = `${words} percent`;
  }
  return {
    kind: "sentence-start",
    suggestion,
    coverAfter: length,
  };
}

/** "one year later" and "one day" read as idioms; every other "one + unit" is checked. */
const IDIOM_UNITS: ReadonlySet<string> = new Set([
  "day",
  "week",
  "month",
  "year",
  "second",
  "days",
]);

function abbreviatedUnitVerdict(item: Item, unit: string): Verdict {
  const text = unit.trim();
  const space = text.startsWith("°") ? "" : " ";
  return {
    kind: "unit",
    suggestion: `${numeralText(item)}${space}${text}`,
    coverAfter: unit.length,
  };
}

function unitVerdict(context: Context, style: NumberStyle): Verdict | null {
  const { item, after, nextWord } = context;
  if (!item.isWords) {
    return null;
  }
  const abbreviated = RE_ABBREVIATED_UNIT.exec(after);
  if (abbreviated) {
    return abbreviatedUnitVerdict(item, abbreviated[0]);
  }
  if (style !== "apa" || !FULL_UNITS.has(nextWord)) {
    return null;
  }
  if (item.value === 1 && IDIOM_UNITS.has(nextWord)) {
    return null;
  }
  const unit = RE_NEXT_WORD.exec(after);
  if (!unit) {
    return null;
  }
  const number = numeralText(item);
  const joiner = unit[0].includes("-") ? "-" : " ";
  const money = MONEY_WORDS.has(nextWord) && nextWord.startsWith("dollar");
  return {
    kind: "unit",
    suggestion: money ? `$${number}` : `${number}${joiner}${unit[1]}`,
    coverAfter: unit[0].length,
  };
}

function ordinalVerdict(item: Item, style: NumberStyle): Verdict | null {
  if (item.value < 1) {
    return null;
  }
  if (style === "apa") {
    if (!item.isWords && item.value < 10) {
      return {
        kind: "ordinal",
        suggestion: ordinalToWords(item.value),
        coverAfter: 0,
      };
    }
    if (item.isWords && item.value >= 10) {
      return {
        kind: "ordinal",
        suggestion: ordinalNumeral(item.value),
        coverAfter: 0,
      };
    }
    return null;
  }
  if (!item.isWords && item.value <= 100) {
    return {
      kind: "ordinal",
      suggestion: ordinalToWords(item.value),
      coverAfter: 0,
    };
  }
  return null;
}

const RE_WORD_SEPARATOR = /[\s-]+/;
const SCALE_WORDS: ReadonlySet<string> = new Set([
  "hundred",
  "thousand",
  "million",
  "billion",
]);

/** A round multiple Chicago allows in words: two hundred, thirty-five hundred, fifty thousand, three million. */
function isRoundMultiple(item: Item): boolean {
  const parts = item.text.toLowerCase().split(RE_WORD_SEPARATOR);
  return (
    item.value > 100 && parts.length <= 3 && SCALE_WORDS.has(parts.at(-1) ?? "")
  );
}

function sizeVerdict(context: Context, style: NumberStyle): Verdict | null {
  const { item } = context;
  if (item.decimal || (item.value === 1 && item.isWords)) {
    return null;
  }
  if (style === "apa") {
    if (!item.isWords && item.value < 10) {
      return {
        kind: "spell-out",
        suggestion: numberToWords(item.value),
        coverAfter: 0,
      };
    }
    if (item.isWords && item.value >= 10 && !context.sentenceStart) {
      return {
        kind: "use-numeral",
        suggestion: numeralText(item),
        coverAfter: 0,
      };
    }
    return null;
  }
  if (!item.isWords && item.value <= 100) {
    return {
      kind: "spell-out",
      suggestion: numberToWords(item.value),
      coverAfter: 0,
    };
  }
  if (
    item.isWords &&
    item.value > 100 &&
    !isRoundMultiple(item) &&
    !context.sentenceStart
  ) {
    return {
      kind: "use-numeral",
      suggestion: numeralText(item),
      coverAfter: 0,
    };
  }
  return null;
}

function verdictFor(context: Context, style: NumberStyle): Verdict | null {
  const { item } = context;
  if (item.isWords && (context.sentenceStart || item.upperInitial)) {
    return null;
  }
  if (context.sentenceStart && !item.isWords) {
    return RE_LIST_MARKER.test(context.after) ? null : startVerdict(context);
  }
  const percent = percentVerdict(context, style);
  if (percent || percentLength(context.after) > 0) {
    return percent;
  }
  if (
    isExemptContext(context) &&
    !(item.isWords && RE_ABBREVIATED_UNIT.test(context.after))
  ) {
    return null;
  }
  if (item.ordinal) {
    return ordinalVerdict(item, style);
  }
  const unit = unitVerdict(context, style);
  if (unit) {
    return unit;
  }
  if (isApaExempt(context, style)) {
    return null;
  }
  return sizeVerdict(context, style);
}

/** APA keeps numerals before a unit word or money word, and after "age". */
function isApaExempt(context: Context, style: NumberStyle): boolean {
  if (style !== "apa" || context.item.isWords) {
    return false;
  }
  return (
    FULL_UNITS.has(context.nextWord) || AGE_LABELS.has(context.previousWord)
  );
}

/* ------------------------------------------------------------------ report */

const KINDS: readonly NumberIssueKind[] = [
  "spell-out",
  "use-numeral",
  "sentence-start",
  "percent",
  "unit",
  "ordinal",
];

export interface NumberOptions {
  style?: NumberStyle;
  /** Skip numbers inside quotation marks. Default true. */
  ignoreQuotes?: boolean;
}

function ignoredSpans(text: string, ignoreQuotes: boolean): Span[] {
  const spans: Span[] = [...text.matchAll(RE_IGNORED)].map((match) => ({
    start: match.index,
    end: match.index + match[0].length,
  }));
  return ignoreQuotes ? [...spans, ...quotedSpans(text)] : spans;
}

function sentenceFor(
  sentences: readonly Sentence[],
  offset: number
): { sentence: Sentence; index: number } | null {
  for (const [index, sentence] of sentences.entries()) {
    if (offset >= sentence.start && offset < sentence.end) {
      return { sentence, index };
    }
  }
  return null;
}

export function checkNumberStyle(
  text: string,
  options: NumberOptions = {}
): NumberReport {
  const style = options.style ?? "apa";
  const skip = ignoredSpans(text, options.ignoreQuotes !== false);
  const sentences = splitSentences(text);
  const headings = headingFlags(sentences);
  const items = [...numeralItems(text), ...wordItems(text)].sort(
    (a, b) => a.start - b.start
  );
  const issues: NumberIssue[] = [];
  let numerals = 0;
  let inWords = 0;
  for (const item of items) {
    const found = sentenceFor(sentences, item.start);
    if (
      !found ||
      headings[found.index] ||
      insideAny(skip, item.start, item.end)
    ) {
      continue;
    }
    if (!item.isWords) {
      numerals++;
    } else if (!(item.ordinal || item.value === 1)) {
      inWords++;
    }
    const context = contextOf(text, item, found.sentence);
    const verdict = verdictFor(context, style);
    if (verdict) {
      const end = Math.min(found.sentence.end, item.end + verdict.coverAfter);
      issues.push({
        start: item.start,
        end,
        text: text.slice(item.start, end),
        kind: verdict.kind,
        suggestion: verdict.suggestion,
        sentenceStart: found.sentence.start,
        sentence: found.sentence.text,
      });
    }
  }
  const byKind = Object.fromEntries(KINDS.map((kind) => [kind, 0])) as Record<
    NumberIssueKind,
    number
  >;
  for (const issue of issues) {
    byKind[issue.kind]++;
  }
  return {
    words: countWords(text),
    sentences: sentences.filter((_, i) => !headings[i]).length,
    numerals,
    inWords,
    issues,
    byKind,
  };
}
